import { modules } from "@history-sim/content/module-catalog";
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  APP_VERSION,
  CONTENT_VERSION,
  type ParticipantProfile,
  useAppStore,
} from "../state/appStore";
import { LearnerApiHttpError, LearnerApiProtocolError } from "../sync/learnerApiClient";
import type { ConnectLearnerResult } from "../sync/learnerSyncCoordinator";
import {
  connectLearnerForSync,
  learnerSyncConfiguration,
  markLocalOnlyLearnerSession,
  recoverLearnerForSync,
} from "../sync/learnerSyncRuntime";
import { BrandMark } from "./BrandMark";

const emptyProfile: ParticipantProfile = {
  name: "",
  major: "",
  studentId: "",
  className: "",
};

const landingKeywordCloud = [
  { label: "中流砥柱", tier: "core", x: 50, y: 49, rotate: 0 },
  { label: "全面抗战路线", tier: "primary", x: 50, y: 25, rotate: -2 },
  { label: "持久战", tier: "primary", x: 20, y: 36, rotate: -5 },
  { label: "抗日民族统一战线", tier: "primary", x: 76, y: 36, rotate: 4 },
  { label: "敌后战场", tier: "primary", x: 18, y: 59, rotate: 4 },
  { label: "人民战争", tier: "primary", x: 82, y: 64, rotate: -4 },
  { label: "毛泽东思想", tier: "primary", x: 50, y: 68, rotate: 2 },
  { label: "抗日根据地", tier: "secondary", x: 50, y: 7, rotate: 0 },
  { label: "百团大战", tier: "secondary", x: 27, y: 15, rotate: -5 },
  { label: "大生产运动", tier: "secondary", x: 73, y: 15, rotate: 5 },
  { label: "党的建设", tier: "secondary", x: 17, y: 69, rotate: -3 },
  { label: "延安整风", tier: "secondary", x: 83, y: 69, rotate: 3 },
  { label: "新民主主义", tier: "secondary", x: 26, y: 78, rotate: 4 },
  { label: "党的七大", tier: "secondary", x: 74, y: 78, rotate: -4 },
  { label: "全面反攻", tier: "secondary", x: 22, y: 84, rotate: -5 },
  { label: "抗战胜利", tier: "secondary", x: 78, y: 84, rotate: 5 },
  { label: "广西学生军", tier: "extension", x: 35, y: 88, rotate: 2 },
  { label: "八路军桂林办事处", tier: "extension", x: 50, y: 93, rotate: 0 },
  { label: "桂林抗战文化", tier: "extension", x: 65, y: 88, rotate: -2 },
] as const;

const dialogFocusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getDialogFocusableElements(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(dialogFocusableSelector)).filter(
    (element) => !element.hidden && element.getAttribute("aria-hidden") !== "true",
  );
}

function learnerConnectionMessage(error: unknown, mode: "join" | "recover"): string {
  if (error instanceof LearnerApiHttpError) {
    if (error.status === 401 || error.status === 403) {
      return mode === "recover"
        ? "恢复码无效、已使用或已过期，请改用最新恢复码。"
        : "邀请码无效或已失效，请向教师核对。";
    }
    if (error.status === 409) return "该学号与现有班级档案冲突，请联系教师核验。";
    if (error.status === 429) return "连接请求较多，请稍后再试。";
    return "在线班级暂时无法建立研学档案，请稍后重试。";
  }
  if (error instanceof LearnerApiProtocolError) {
    return error.code === "NETWORK_UNAVAILABLE" || error.code === "REQUEST_TIMEOUT"
      ? "当前无法连接教学服务，请检查网络后重试。"
      : "教学服务返回异常，为保护学习数据已停止连接。";
  }
  return "暂时无法建立在线研学档案，请稍后重试。";
}

interface LandingProps {
  readonly syncMode?: "local-only" | "api";
  readonly connectLearner?: typeof connectLearnerForSync;
  readonly recoverLearner?: typeof recoverLearnerForSync;
}

export function Landing({
  syncMode = learnerSyncConfiguration.mode,
  connectLearner = connectLearnerForSync,
  recoverLearner = recoverLearnerForSync,
}: LandingProps = {}) {
  const [profile, setProfile] = useState(emptyProfile);
  const [inviteCode, setInviteCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [entryMode, setEntryMode] = useState<"join" | "recover">("join");
  const [pendingConnection, setPendingConnection] = useState<ConnectLearnerResult | null>(null);
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false);
  const [copyNotice, setCopyNotice] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const openFormButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const recoveryInputRef = useRef<HTMLInputElement>(null);
  const restoreFocusOnClose = useRef(false);
  const startSession = useAppStore((state) => state.startSession);
  const startServerSession = useAppStore((state) => state.startServerSession);
  const clientSessionId = useAppStore((state) => state.sessionId);
  const apiMode = syncMode === "api";

  const closeForm = useCallback(() => {
    restoreFocusOnClose.current = true;
    setFormOpen(false);
  }, []);

  useEffect(() => {
    if (formOpen) {
      if (pendingConnection) return;
      if (entryMode === "recover") recoveryInputRef.current?.focus();
      else nameInputRef.current?.focus();
      return;
    }
    if (restoreFocusOnClose.current) {
      restoreFocusOnClose.current = false;
      openFormButtonRef.current?.focus();
    }
  }, [entryMode, formOpen, pendingConnection]);

  useEffect(() => {
    if (!formOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeForm();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [closeForm, formOpen]);

  const keepFocusInDialog = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = getDialogFocusableElements(dialog);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const active = document.activeElement;
    if (!dialog.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const update = (field: keyof ParticipantProfile, value: string) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitting(true);
    const normalized = {
      name: profile.name.trim(),
      major: profile.major.trim(),
      studentId: profile.studentId.trim(),
    };
    try {
      if (apiMode) {
        const connected =
          entryMode === "recover"
            ? await recoverLearner({
                clientSessionId,
                contentVersion: CONTENT_VERSION,
                appVersion: APP_VERSION,
                recoveryCode: recoveryCode.trim(),
              })
            : await connectLearner({
                clientSessionId,
                contentVersion: CONTENT_VERSION,
                appVersion: APP_VERSION,
                identity: { ...normalized, inviteCode: inviteCode.trim() },
              });
        if (!connected) throw new Error("Online learner runtime is unavailable");
        setPendingConnection(connected);
        setRecoveryAcknowledged(false);
        setCopyNotice("");
      } else {
        markLocalOnlyLearnerSession(clientSessionId);
        startSession({ ...normalized, className: profile.className.trim() });
      }
    } catch (error) {
      setSubmitError(learnerConnectionMessage(error, entryMode));
    } finally {
      setSubmitting(false);
    }
  };

  const enterConnectedSession = () => {
    if (!pendingConnection || !recoveryAcknowledged) return;
    startServerSession(
      {
        ...pendingConnection.profile,
        className: pendingConnection.enrollment.classroomName,
      },
      pendingConnection.progress,
    );
  };

  const copyRecoveryCode = async () => {
    if (!pendingConnection) return;
    try {
      await navigator.clipboard.writeText(pendingConnection.recovery.code);
      setCopyNotice("恢复码已复制。请保存到受控位置。");
    } catch {
      setCopyNotice("浏览器未允许自动复制，请手动选择并复制恢复码。");
    }
  };

  const startDemo = () => {
    markLocalOnlyLearnerSession(clientSessionId);
    startSession({
      name: "演示研学员",
      major: "思想政治教育",
      studentId: "DEMO-001",
      className: "大赛演示班",
    });
  };

  return (
    <main id="main-content" className="landing-shell">
      <div className="landing-noise" aria-hidden="true" />
      <header className="landing-header">
        <BrandMark />
      </header>

      <section className="landing-hero">
        <div className="landing-hero__copy">
          <div className="hero-index">
            <span>CHAPTER</span>
            <strong>03</strong>
          </div>
          <p className="hero-kicker">全民族抗日战争的中流砥柱</p>
          <h1 className="landing-hero__title">
            <span className="landing-hero__title-line">让史料彼此印证，</span>
            <span className="landing-hero__title-line landing-hero__title-line--accent">
              让结论由你抵达。
            </span>
          </h1>
          <p className="hero-summary">
            你将以“抗战史料研学员”的身份，穿行于六个研学模块、十八个历史场景，
            通过观察、判断和证据建构回答一个核心问题：为什么中国共产党能够成为全民族抗战的中流砥柱？
          </p>
          <div className="hero-actions">
            <button
              ref={openFormButtonRef}
              className="primary-button"
              type="button"
              onClick={() => {
                restoreFocusOnClose.current = false;
                setFormOpen(true);
              }}
            >
              <span>进入标准研学</span>
              <b aria-hidden="true">→</b>
            </button>
            <button className="text-button" type="button" onClick={startDemo}>
              使用合成身份快速体验
            </button>
          </div>
          <div className="hero-proof">
            <div>
              <strong>06</strong>
              <span>研学模块</span>
            </div>
            <div>
              <strong>18</strong>
              <span>独立场景</span>
            </div>
            <div>
              <strong>19</strong>
              <span>正式任务</span>
            </div>
            <div>
              <strong>4端</strong>
              <span>真实验证目标</span>
            </div>
          </div>
        </div>

        <div className="landing-hero__stage" aria-hidden="true">
          <div className="stage-orbit stage-orbit--outer" />
          <div className="stage-orbit stage-orbit--inner" />
          <div className="stage-map-lines">
            <svg viewBox="0 0 620 620" aria-hidden="true">
              <path d="M72 390C154 298 217 338 290 245S432 119 551 184" />
              <path d="M91 439C211 425 239 481 349 385S464 309 564 351" />
              <path d="M144 173C199 221 259 176 324 221S453 241 511 274" />
              <circle cx="72" cy="390" r="5" />
              <circle cx="290" cy="245" r="5" />
              <circle cx="551" cy="184" r="5" />
              <circle cx="349" cy="385" r="5" />
            </svg>
          </div>
          <div className="stage-word-cloud" data-testid="landing-keyword-cloud">
            {landingKeywordCloud.map((keyword) => (
              <span
                key={keyword.label}
                data-tier={keyword.tier}
                style={
                  {
                    "--cloud-x": `${keyword.x}%`,
                    "--cloud-y": `${keyword.y}%`,
                    "--cloud-rotate": `${keyword.rotate}deg`,
                  } as CSSProperties
                }
              >
                {keyword.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="module-ribbon" aria-label="六个研学模块">
        {modules.map((module) => (
          <div key={module.id} className="module-ribbon__item">
            <span>{String(module.order).padStart(2, "0")}</span>
            <p>{module.title}</p>
          </div>
        ))}
      </section>

      {formOpen && (
        <div className="modal-backdrop">
          <section
            ref={dialogRef}
            className="identity-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="identity-title"
            aria-describedby="identity-description"
            tabIndex={-1}
            onKeyDown={keepFocusInDialog}
          >
            <button className="modal-close" type="button" onClick={closeForm} aria-label="关闭">
              ×
            </button>
            <span className="eyebrow">建立研学档案</span>
            <h2 id="identity-title">
              {pendingConnection ? "保存新的跨设备恢复码" : "以抗战史料研学员身份进入"}
            </h2>
            <p id="identity-description">
              {pendingConnection
                ? "恢复码只显示在当前步骤；旧恢复码已经失效。保存后再进入课程地图。"
                : apiMode
                  ? "字段仅用于教学评价与班级归属；姓名、专业和学号通过受控教学服务直接提交，不写入离线同步队列。"
                  : "字段仅用于教学评价与班级归属，只保留在当前标签页会话，不上传教学服务。"}
            </p>
            {pendingConnection ? (
              <div>
                <div className="form-grid">
                  <label>
                    <span>新的恢复码</span>
                    <input
                      value={pendingConnection.recovery.code}
                      readOnly
                      aria-describedby="recovery-code-expiry"
                    />
                  </label>
                </div>
                <p id="recovery-code-expiry">
                  有效至：{new Date(pendingConnection.recovery.expiresAt).toLocaleString("zh-CN")}。
                  每次恢复或重新签发后，旧码立即失效。
                </p>
                <button className="secondary-button" type="button" onClick={copyRecoveryCode}>
                  复制恢复码
                </button>
                {copyNotice && <p role="status">{copyNotice}</p>}
                <label className="consent-row">
                  <input
                    type="checkbox"
                    checked={recoveryAcknowledged}
                    onChange={(event) => setRecoveryAcknowledged(event.target.checked)}
                  />
                  <span>我已将新恢复码保存到受控位置，并理解旧恢复码不能再次使用。</span>
                </label>
                <button
                  className="primary-button primary-button--wide"
                  type="button"
                  disabled={!recoveryAcknowledged}
                  onClick={enterConnectedSession}
                >
                  已保存，进入研学 <b aria-hidden="true">→</b>
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                {apiMode && (
                  <fieldset className="hero-actions" aria-label="身份验证方式">
                    <button
                      className={entryMode === "join" ? "primary-button" : "secondary-button"}
                      type="button"
                      aria-pressed={entryMode === "join"}
                      onClick={() => {
                        setEntryMode("join");
                        setSubmitError("");
                      }}
                    >
                      邀请码加入
                    </button>
                    <button
                      className={entryMode === "recover" ? "primary-button" : "secondary-button"}
                      type="button"
                      aria-pressed={entryMode === "recover"}
                      onClick={() => {
                        setEntryMode("recover");
                        setSubmitError("");
                      }}
                    >
                      跨设备恢复
                    </button>
                  </fieldset>
                )}
                {apiMode && entryMode === "recover" ? (
                  <div className="form-grid">
                    <label>
                      <span>最新恢复码</span>
                      <input
                        ref={recoveryInputRef}
                        value={recoveryCode}
                        onChange={(event) => setRecoveryCode(event.target.value)}
                        placeholder="REC-…"
                        autoComplete="off"
                        required
                      />
                    </label>
                  </div>
                ) : (
                  <div className="form-grid">
                    <label>
                      <span>姓名</span>
                      <input
                        ref={nameInputRef}
                        value={profile.name}
                        onChange={(event) => update("name", event.target.value)}
                        placeholder="请输入姓名"
                        autoComplete="name"
                        required
                      />
                    </label>
                    <label>
                      <span>专业</span>
                      <input
                        value={profile.major}
                        onChange={(event) => update("major", event.target.value)}
                        placeholder="例如：思想政治教育"
                        required
                      />
                    </label>
                    <label>
                      <span>学号</span>
                      <input
                        value={profile.studentId}
                        onChange={(event) => update("studentId", event.target.value)}
                        placeholder="请输入学号"
                        autoComplete="off"
                        required
                      />
                    </label>
                    <label htmlFor="class-or-invite-code">
                      <span>{apiMode ? "班级邀请码" : "班级"}</span>
                      {apiMode ? (
                        <input
                          id="class-or-invite-code"
                          value={inviteCode}
                          onChange={(event) => setInviteCode(event.target.value)}
                          placeholder="请输入教师提供的邀请码"
                          autoComplete="one-time-code"
                          required
                        />
                      ) : (
                        <input
                          id="class-or-invite-code"
                          value={profile.className}
                          onChange={(event) => update("className", event.target.value)}
                          placeholder="请输入课程班级"
                          required
                        />
                      )}
                    </label>
                  </div>
                )}
                <label className="consent-row">
                  <input type="checkbox" required />
                  <span>
                    我已知悉：学习行为用于形成个人反馈；对外证据默认匿名导出；恢复码只在当前步骤显示，关闭共享设备前应安全退出。
                  </span>
                </label>
                {submitError && (
                  <p className="identity-error" role="alert">
                    {submitError}
                  </p>
                )}
                <button
                  className="primary-button primary-button--wide"
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting
                    ? entryMode === "recover"
                      ? "正在恢复研学档案…"
                      : "正在建立研学档案…"
                    : entryMode === "recover"
                      ? "验证并恢复"
                      : "建立档案并进入"}{" "}
                  {!submitting && <b aria-hidden="true">→</b>}
                </button>
              </form>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
